import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import useUnsavedChanges, { UnsavedChangesDialog } from '../lib/useUnsavedChanges';
import './WorkOrders.css';

const WorkOrderForm = ({ workOrderId, workOrderData, onSave, isAdmin, isReadOnly,
  startEditing = false }) => {
  const [formData, setFormData] = useState({
    description: '',
    allocated_hours: 0,
    status: 'draft',
    notes: '',
    contractor_id: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  /*
   * Open on the form, not on a button that opens the form.
   *
   * This page reaches the component inside a fold labelled "Edit work order",
   * so getting to a field took two clicks - open the fold, then press Edit -
   * and between them sat a read-only copy of the card at the top of the page.
   * With that copy gone the fold held one button, which is a bar you click to
   * reveal a bar you click. The caller says whether this person can edit, and
   * when they can the form is what the section is for.
   */
  const [isEditing, setIsEditing] = useState(startEditing);
  const [contractors, setContractors] = useState([]);

  // What the fields say against what the work order says (or the blank form
  // for a new one): while they differ and the form is open, leaving asks
  // first (lib/useUnsavedChanges). Released on save.
  const baseline = (wo) => ({
    description: wo?.description || '',
    allocated_hours: wo?.allocated_hours || 0,
    status: wo?.status || 'draft',
    notes: wo?.notes || '',
    contractor_id: wo?.contractor_id || ''
  });
  const dirty = isEditing && !loading
    && JSON.stringify(formData) !== JSON.stringify(baseline(workOrderData));
  const leave = useUnsavedChanges(dirty, { what: 'This work order' });

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Fetch contractors for dropdown (admin only)
  useEffect(() => {
    if (isAdmin && isEditing) {
      const fetchContractors = async () => {
        try {
          const response = await axios.get(
            `${API_BASE}/contractors`
          );
          setContractors(response.data || []);
        } catch (err) {
          console.error('Error fetching contractors:', err);
        }
      };
      fetchContractors();
    }
  }, [isAdmin, isEditing, API_BASE]);

  // Initialize form with work order data when component loads or data changes
  useEffect(() => {
    if (workOrderData) {
      setFormData({
        description: workOrderData.description || '',
        allocated_hours: workOrderData.allocated_hours || 0,
        status: workOrderData.status || 'draft',
        notes: workOrderData.notes || '',
        contractor_id: workOrderData.contractor_id || '',
      });
    }
  }, [workOrderData]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'allocated_hours' ? parseFloat(value) || 0 : value
    }));
  }, []);

  const validateForm = () => {
    if (!formData.description.trim()) {
      setError('Description is required');
      return false;
    }
    if (formData.allocated_hours <= 0) {
      setError('Allocated hours must be greater than 0');
      return false;
    }
    if (isAdmin && !formData.contractor_id) {
      setError('Contractor is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const method = workOrderId ? 'put' : 'post';
      const url = workOrderId 
        ? `${API_BASE}/work-orders/${workOrderId}`
        : `${API_BASE}/work-orders`;

      const response = await axios({
        method,
        url,
        data: formData
      });

      leave.release();
      setSuccess(true);
      setIsEditing(false);
      
      // Auto-dismiss success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
      
      // Notify parent component
      if (onSave) {
        onSave(response.data);
      }
    } catch (err) {
      console.error('Error saving work order:', err);
      setError(err.response?.data?.message || 'Failed to save work order');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => leave.guard(() => {
    setIsEditing(false);
    if (workOrderData) setFormData(baseline(workOrderData));
  });

  const isFormDisabled = isReadOnly && !isAdmin;

  return (
    <div className="work-order-form-container">
      <UnsavedChangesDialog leave={leave} />
      {!isEditing && !isReadOnly && (
        <button 
          className="btn-edit-work-order"
          onClick={() => setIsEditing(true)}
        >
          ✏️ Edit
        </button>
      )}

      {isEditing && !isFormDisabled && (
        <form className="work-order-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Work order description"
              rows={3}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="allocated_hours">Allocated Hours *</label>
              <input
                id="allocated_hours"
                type="number"
                name="allocated_hours"
                value={formData.allocated_hours}
                onChange={handleChange}
                placeholder="0"
                step="0.5"
                min="0"
                required
              />
            </div>

            {isAdmin && (
              <div className="form-group">
                <label htmlFor="contractor_id">Contractor *</label>
                <select
                  id="contractor_id"
                  name="contractor_id"
                  value={formData.contractor_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a contractor</option>
                  {contractors.map(contractor => (
                    <option key={contractor.id} value={contractor.id}>
                      {contractor.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {isAdmin && (
              <div className="form-group">
                <label htmlFor="status">Status *</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <option value="draft">Draft</option>
                  <option value="issued">Issued</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Additional notes (optional)"
              rows={2}
            />
          </div>

          {error && (
            <div className="form-error">
              ⚠️ {error}
            </div>
          )}

          {success && (
            <div className="form-success">
              ✅ Work order saved successfully
            </div>
          )}

          <div className="form-actions">
            <button 
              type="submit" 
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
            <button 
              type="button" 
              className="btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/*
        * What this showed when it was not being edited: Description, Allocated
        * Hours and Status. All three are already on the page above it - the
        * description IS the heading, the allocated figure is the first of the
        * four hours, and the status is the badge in the top line - so opening
        * "Edit work order" showed you a second, smaller copy of the card you
        * were already looking at, with the Edit button underneath. Nobody was
        * misled by it; it just read as though the page had lost its place.
        *
        * Notes were the one thing here that appears nowhere else, so they stay.
        */}
      {!isEditing && formData.notes && (
        <div className="work-order-view">
          <div className="view-section">
            <h3>Notes</h3>
            <p>{formData.notes}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkOrderForm;
